<template>
    <div class="distribution_card">
        <div class="card_header is-flex is-align-items-center">
            <PieChartIcon class="header_icon" />
            <span class="card_title">{{ title }}</span>
        </div>

        <div v-if="hasDistributions" class="distributions_grid"
            :class="{ 'is-single': distributions.length === 1 }">
            <div v-for="dist in distributions" :key="dist.category_id"
                class="distribution_col">
                <div class="col_label">{{ dist.category_label }}</div>
                <div class="donut_container">
                    <EChart v-if="chartReady" :option="chartOption(dist)"
                        @chart-mouseover="onChartHover(dist, $event)"
                        @chart-mouseout="onChartLeave" />
                </div>
                <ul class="ranking_list">
                    <li v-for="(item, idx) in dist.items" :key="item.name"
                        class="ranking_item"
                        :class="{ 'is-focused': isFocused(dist, item.name) }"
                        @mouseenter="onItemEnter(dist, item.name)"
                        @mouseleave="onItemLeave">
                        <span class="ranking_label">{{ item.name }}</span>
                        <div class="ranking_bar_track">
                            <div class="ranking_bar_fill"
                                :style="{
                                    width: barWidth(item.share),
                                    background: palette(dist)[idx],
                                }"></div>
                        </div>
                        <span class="ranking_meta">
                            <span class="ranking_amount">{{ formatMoney(item.amount) }}</span>
                            <span class="ranking_dot" aria-hidden="true">·</span>
                            <span class="ranking_share">{{ formatShare(item.share) }}</span>
                        </span>
                    </li>
                </ul>
            </div>
        </div>
        <p v-else class="empty_state">
            Sin categorías clasificadas para mostrar distribución.
        </p>
    </div>
</template>

<script>
import EChart from "@/components/charts/EChart.vue";
import PieChartIcon from "@/components/svg/PieChartIcon.vue";
import { parseToMoney } from "@/utils/currency";
import { interpolateHexPalette } from "@/utils/colorPalette";

// Anchor tokens — leídos por getComputedStyle en mounted porque el canvas
// de ECharts no resuelve var(...). Mantener este par como los extremos del
// gradiente del que se interpolan N colores; el theme define el rango.
const LIGHTEST_ANCHOR = "--report-chart-segment-3";
const DARKEST_ANCHOR = "--report-chart-segment-1";

export default {
    name: "StoresDistributionCard",
    components: { EChart, PieChartIcon },
    props: {
        // Lista de distribuciones — una entrada por categoría configurada.
        // Backend devuelve hasta 2. Cada entry: {category_id, category_label,
        // items: [{name, amount, share}]}.
        distributions: {
            type: Array,
            required: true,
            validator: (arr) => arr.every(
                (d) => typeof d.category_id === "string" && Array.isArray(d.items),
            ),
        },
    },
    data() {
        // chartReady arranca false porque necesitamos resolver los anchor
        // tokens vía getComputedStyle antes del primer render. Evita el flash
        // del donut sin paleta y el caso "los anchors no existen" (theme mal
        // configurado): si el palette queda vacío, no rendereamos el chart.
        // `focusedCategory`: cuando un item está hovereado (chart o lista),
        // emitimos arriba qué categoría + valor, para que el ranking de
        // clientes resalte las contrapartes asociadas a ese atributo.
        return {
            chartReady: false,
            lightAnchor: "",
            darkAnchor: "",
            focusedCategory: null, // { category_id, value } | null
        };
    },
    computed: {
        title() {
            // Cuando hay >1 distribución, el título no puede ser "por X" — usa
            // un genérico. Single distribución conserva el patrón anterior.
            if (this.distributions.length === 1) {
                return `Distribución de ventas por ${this.distributions[0].category_label || "categoría"}`;
            }
            return "Distribución de ventas por categoría";
        },
        hasDistributions() {
            return this.distributions.some((d) => d.items.length > 0);
        },
    },
    mounted() {
        this.resolveCssColors();
        this.chartReady = !!this.lightAnchor && !!this.darkAnchor;
    },
    methods: {
        resolveCssColors() {
            if (typeof window === "undefined") return;
            const root = getComputedStyle(document.documentElement);
            this.lightAnchor = root.getPropertyValue(LIGHTEST_ANCHOR).trim();
            this.darkAnchor = root.getPropertyValue(DARKEST_ANCHOR).trim();
        },
        palette(dist) {
            // N colores interpolados entre dark y light — el item con más
            // share va más claro, los menores más oscuros (matchea el donut).
            if (!this.lightAnchor || !this.darkAnchor) return [];
            return interpolateHexPalette(
                this.lightAnchor, this.darkAnchor, dist.items.length,
            );
        },
        chartOption(dist) {
            const palette = this.palette(dist);
            return {
                tooltip: {
                    trigger: "item",
                    formatter: (params) => {
                        const share = Math.round(params.percent ?? 0);
                        return `<div class="echart_tooltip">
                            <strong>${params.name}</strong><br>
                            ${parseToMoney(params.value)} · ${share}%
                        </div>`;
                    },
                },
                series: [
                    {
                        type: "pie",
                        radius: ["60%", "90%"],
                        padAngle: 2,
                        avoidLabelOverlap: false,
                        label: { show: false },
                        labelLine: { show: false },
                        itemStyle: { borderRadius: 0 },
                        data: dist.items.map((s, i) => ({
                            value: s.amount,
                            name: s.name,
                            itemStyle: { color: palette[i] },
                        })),
                    },
                ],
            };
        },
        // Hover handlers — chart (mouseover sobre slice) y lista (mouseenter
        // sobre item) convergen al mismo state, así el highlight es
        // consistente. mouseleave en cualquiera limpia.
        onChartHover(dist, params) {
            // params.name = label del slice hovereado.
            this.setFocus(dist.category_id, params.name);
        },
        onChartLeave() {
            this.clearFocus();
        },
        onItemEnter(dist, value) {
            this.setFocus(dist.category_id, value);
        },
        onItemLeave() {
            this.clearFocus();
        },
        setFocus(categoryId, value) {
            this.focusedCategory = { category_id: categoryId, value };
            this.$emit("focus-category", this.focusedCategory);
        },
        clearFocus() {
            this.focusedCategory = null;
            this.$emit("blur-category");
        },
        isFocused(dist, value) {
            return this.focusedCategory
                && this.focusedCategory.category_id === dist.category_id
                && this.focusedCategory.value === value;
        },
        sharePercent(share) {
            const v = Math.max(0, Math.min(1, share ?? 0));
            return Math.round(v * 100);
        },
        barWidth(share) {
            return `${this.sharePercent(share)}%`;
        },
        formatMoney(v) {
            return parseToMoney(v);
        },
        formatShare(share) {
            return `${this.sharePercent(share)}%`;
        },
    },
};
</script>

<style scoped lang="scss">
.distribution_card {
    background: var(--report-bg);
    border-radius: 16px;
    border: 1px solid var(--report-border);
    box-shadow: 0 1px 2px rgba(10, 13, 20, 0.03);
    padding: 20px;
    height: 100%;
    display: flex;
    flex-direction: column;
    overflow: hidden;
}

.card_header {
    margin-bottom: 12px;
    color: var(--report-text);
    flex-shrink: 0;

    .header_icon {
        margin-right: 8px;
        flex-shrink: 0;
    }

    .card_title {
        font-size: 14px;
        font-weight: 600;
    }
}

// Grid de distribuciones: hasta 2 columnas. `is-single` (cuando hay sólo
// una categoría) colapsa a una columna para no dejar el donut huérfano
// con espacio vacío al lado.
.distributions_grid {
    flex: 1;
    min-height: 0;
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 16px;

    &.is-single {
        grid-template-columns: 1fr;
    }
}

.distribution_col {
    display: flex;
    flex-direction: column;
    min-width: 0;
    min-height: 0;
}

.col_label {
    font-size: 12px;
    font-weight: 600;
    color: var(--report-text-muted-strong);
    text-transform: capitalize;
    margin-bottom: 4px;
    flex-shrink: 0;
}

// Donut altura fija — antes era 240px, ahora más chico porque hay dos
// donuts a compartir el alto disponible. Lista crece con el sobrante.
.donut_container {
    flex-shrink: 0;
    height: 140px;
    margin-bottom: 8px;
}

.ranking_list {
    list-style: none;
    margin: 0;
    padding: 0;
    flex: 1;
    min-height: 0;
    overflow-y: auto;
}

.ranking_item {
    display: grid;
    // Más comprimido que el ranking de clientes (3 col grid), porque dos
    // columnas conviven en la misma card.
    grid-template-columns: minmax(48px, 96px) 1fr auto;
    gap: 8px;
    align-items: center;
    padding: 4px 6px;
    font-size: 12px;
    border-radius: 6px;
    cursor: pointer;
    transition: background-color 120ms ease;

    &:hover,
    &.is-focused {
        background: var(--report-surface);
    }
}

.ranking_label {
    color: var(--report-text);
    font-weight: 500;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.ranking_bar_track {
    background: var(--report-progress-bg);
    border-radius: 999px;
    height: 10px;
    overflow: hidden;
}

.ranking_bar_fill {
    height: 100%;
    border-radius: 999px;
    transition: width 200ms ease-out;
}

.ranking_meta {
    display: inline-flex;
    align-items: baseline;
    gap: 6px;
    color: var(--report-text-muted);
    font-size: 11px;
    white-space: nowrap;

    .ranking_amount {
        color: var(--report-text-muted-strong);
        font-weight: 500;
    }

    .ranking_dot {
        color: var(--report-text-muted-soft);
        font-weight: 700;
    }

    .ranking_share {
        color: var(--report-text-muted);
        font-weight: 500;
    }
}

.empty_state {
    color: var(--report-text-muted);
    font-size: 13px;
    text-align: center;
    padding: 24px 8px;
    margin: auto;
}
</style>
